import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
	verifyGroupOwnership,
	verifyGroupMembershipPermission,
} from "@/lib/security-utils";
import {
	validateGroupName,
	validateGroupDescription,
} from "@/lib/validation-utils";
import {
	getErrorMessage,
	getUserFriendlyErrorMessage,
} from "@/utils/error-utils";
import {
	FetchMenteesResponse,
	FetchMenteesMentee,
} from "@/types/supabase-functions";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Label,
	Textarea,
	Badge,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogAction,
	AlertDialogCancel,
	Progress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/primitives";
import {
	Plus,
	Edit,
	Trash2,
	Users,
	UserPlus,
	UserMinus,
	Mail,
	Target,
	Calendar,
	Settings,
	CheckCircle,
	AlertTriangle,
	Clock,
	Eye,
} from "lucide-react";

import { TeamMoodOverview } from "@/features/terms/TeamMoodOverview";

interface Group {
	id: string;
	name: string;
	description: string;
	created_at: string;
	member_count: number;
	members: GroupMember[];
}

interface GroupMember {
	id: string;
	mentee_id: string;
	mentee_name: string;
	mentee_email: string;
	created_at: string;
	checkin_count?: number;
}

interface Mentee {
	id: string;
	name: string;
	email: string;
	status: string;
	reflections_count?: number;
	last_activity?: string;
}

interface Topic {
	id: string;
	title: string;
	description: string;
	initial_question?: string;
}

interface TopicAssignment {
	id: string;
	topic_id: string;
	mentee_id: string;
	group_id: string | null;
	assigned_at: string;
	topic: Topic;
	completion_stats: {
		total_mentees: number;
		completed_count: number;
		completion_percentage: number;
	};
}

export function GroupManagement() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [groups, setGroups] = useState<Group[]>([]);
	const [mentees, setMentees] = useState<Mentee[]>([]);
	const [topics, setTopics] = useState<Topic[]>([]);
	const [assignments, setAssignments] = useState<TopicAssignment[]>([]);
	const [loading, setLoading] = useState(true);

	// Dialog states
	const [showCreateDialog, setShowCreateDialog] = useState(false);
	const [showEditDialog, setShowEditDialog] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [showAssignTopicDialog, setShowAssignTopicDialog] = useState(false);

	// Form states
	const [newGroupName, setNewGroupName] = useState("");
	const [newGroupDescription, setNewGroupDescription] = useState("");
	const [editingGroup, setEditingGroup] = useState<Group | null>(null);
	const [deletingGroup, setDeletingGroup] = useState<Group | null>(null);
	const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
	const [selectedMenteeToAdd, setSelectedMenteeToAdd] = useState("");
	const [selectedTopicId, setSelectedTopicId] = useState("");
	const [isCreatingNewTopic, setIsCreatingNewTopic] = useState(false);
	const [newTopicTitle, setNewTopicTitle] = useState("");
	const [newTopicDescription, setNewTopicDescription] = useState("");
	const [
		selectedGroupForMenteeManagement,
		setSelectedGroupForMenteeManagement,
	] = useState<Group | null>(null);

	const [showConfirmRemoveMenteeDialog, setShowConfirmRemoveMenteeDialog] =
		useState(false);
	const [menteeToRemove, setMenteeToRemove] = useState<Mentee | null>(null);

	useEffect(() => {
		fetchData();

		// Set up real-time subscription for relationship changes
		console.log(
			"🔄 GroupManagement: Setting up real-time subscription for mentor relationships",
		);
		const subscription = supabase
			.channel("mentor_relationships_groups")
			.on(
				"postgres_changes",
				{
					event: "*", // Listen to all changes (INSERT, UPDATE, DELETE)
					schema: "public",
					table: "mentor_mentee_relationships",
					filter: `mentor_id=eq.${user?.id}`, // Only listen to this mentor's relationships
				},
				async (payload) => {
					console.log(
						"🔔 GroupManagement: Relationship change detected:",
						payload,
					);
					// Refresh mentees list and groups when any relationship changes
					const updatedMentees = await fetchMentees();
					fetchGroups(updatedMentees);
				},
			)
			.subscribe();

		// Cleanup subscription on unmount
		return () => {
			console.log("🧹 GroupManagement: Cleaning up real-time subscription");
			subscription.unsubscribe();
		};
	}, [user?.id]);

	const fetchData = async () => {
		// Fetch mentees first, then fetch groups (which depends on mentees data)
		const menteesData = await fetchMentees();
		await Promise.all([
			fetchGroups(menteesData),
			fetchTopics(),
			fetchAssignments(),
		]);
		setLoading(false);
	};

	const fetchCheckInCounts = async (menteeIds: string[]) => {
		if (menteeIds.length === 0) return {};

		const thirtyDaysAgo = new Date();
		thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 28);

		const { data: checkInCounts, error } = await supabase
			.from("daily_checkins")
			.select("user_id")
			.in("user_id", menteeIds)
			.gte("created_at", thirtyDaysAgo.toISOString());

		if (error) {
			console.error("Error fetching check-in counts:", error);
			return {};
		}

		// Count check-ins per user
		const counts: Record<string, number> = {};
		checkInCounts?.forEach((checkIn) => {
			counts[checkIn.user_id] = (counts[checkIn.user_id] || 0) + 1;
		});

		return counts;
	};

	const fetchGroups = async (menteesData?: Mentee[]) => {
		try {
			// Ensure authenticated user available
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();
			if (sessionError || !session?.access_token || !user?.id) {
				console.error(
					"No valid session or user for groups fetch",
					sessionError,
				);
				setGroups([]);
				return;
			}

			// Prefer secure edge function to enforce privacy and avoid client-side RLS leakage
			const { data, error } = await supabase.functions.invoke(
				"fetch-mentor-groups",
				{
					headers: { Authorization: `Bearer ${session.access_token}` },
				},
			);

			if (error) {
				console.error("Edge function fetch-mentor-groups error:", error);
				setGroups([]);
				return;
			}

			const apiGroups = (data?.groups || []) as Array<any>;
			const mapped: Group[] = apiGroups.map((g) => ({
				id: g.id,
				name: g.name,
				description: g.description,
				created_at: g.created_at,
				member_count: g.member_count || 0,
				members: Array.isArray(g.members) ? g.members : [],
			}));

			setGroups(mapped);
		} catch (e) {
			console.error("Error fetching groups via function:", e);
			setGroups([]);
		}
	};

	const fetchMentees = async (): Promise<Mentee[]> => {
		try {
			console.log("🔍 GroupManagement: Starting fetchMentees");

			// Get the current session
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			console.log("🔍 Session fetch result:", {
				hasSession: !!session,
				sessionError: sessionError,
				hasAccessToken: !!session?.access_token,
				tokenLength: session?.access_token?.length,
				userEmail: session?.user?.email,
				userId: session?.user?.id,
			});

			if (sessionError || !session) {
				console.error("❌ No valid session found:", sessionError);
				setMentees([]);
				return [];
			}

			console.log("🚀 Calling fetch-mentees edge function with auth header");
			console.log(
				"🚀 Token preview:",
				session.access_token.substring(0, 20) + "...",
			);

			// Use secure edge function to fetch mentees with explicit auth header
			const { data, error } =
				await supabase.functions.invoke<FetchMenteesResponse>("fetch-mentees", {
					headers: {
						Authorization: `Bearer ${session.access_token}`,
					},
				});

			if (error) {
				console.error("❌ Edge function error details:", {
					error: error,
					errorType: typeof error,
					errorMessage: error.message,
					errorCode: error.status,
					errorDetails: error.details,
					errorContext: error.context,
				});
				setMentees([]);
				return [];
			}

			console.log("✅ GroupManagement: Mentees fetched successfully:", data);

			// Filter for accepted mentees only and format for GroupManagement
			const acceptedMentees = (data.mentees || [])
				.filter(
					(mentee: FetchMenteesMentee) =>
						mentee.status === "accepted" || !mentee.status,
				)
				.map((mentee: FetchMenteesMentee) => ({
					id: mentee.id,
					name: mentee.name,
					email: mentee.email,
					status: mentee.status || "accepted",
					reflections_count: mentee.reflections_count || 0,
					last_activity: mentee.last_activity || mentee.created_at,
				}));

			setMentees(acceptedMentees);
			return acceptedMentees;
		} catch (error) {
			console.error("Error fetching mentees:", error);
			setMentees([]);
			return [];
		}
	};

	const fetchTopics = async () => {
		const { data, error } = await supabase
			.from("topics")
			.select("*")
			.eq("created_by", user?.id)
			.order("created_at", { ascending: false });

		if (error) {
			console.error("Error fetching topics:", error.message || error);
			console.error("Full topics error:", JSON.stringify(error, null, 2));
		} else {
			// Map database fields to interface fields
			const mappedTopics = (data || []).map((topic: any) => ({
				id: topic.id,
				title: topic.name, // Map name to title for interface compatibility
				description: topic.description,
				initial_question: topic.prompt_creation || "",
			}));
			setTopics(mappedTopics);
		}
	};

	const fetchAssignments = async () => {
		// Fetch assignments with optional group join - this will include both group and individual assignments
		const { data: basicAssignments, error: basicError } = await supabase
			.from("topic_assignments")
			.select(
				`
        *,
        topics!inner(name, description),
        groups(name)
      `,
			)
			.eq("topics.created_by", user?.id)
			.order("created_at", { ascending: false });

		if (basicError) {
			console.error(
				"Error fetching topic assignments:",
				basicError.message || basicError,
			);
			console.error(
				"Full assignments error:",
				JSON.stringify(basicError, null, 2),
			);
			setAssignments([]);
			return;
		}

		const data = basicAssignments;

		if (!data) {
			setAssignments([]);
			return;
		}

		const formattedAssignments = data.map((item) => {
			return {
				id: item.id,
				topic_id: item.topic_id,
				mentee_id: item.mentee_id, // Include mentee_id for group filtering
				group_id: item.group_id,
				assigned_at: item.created_at, // Use created_at as assigned_at
				topic: {
					id: item.topic_id,
					title: item.topics?.name || "Unknown Topic",
					description: item.topics?.description || "",
				},
				completion_stats: {
					total_mentees: 0,
					completed_count: 0,
					completion_percentage: 0,
				},
			};
		});

		setAssignments(formattedAssignments);
	};

	const createGroup = useCallback(async () => {
		// Validate group name
		const nameValidation = validateGroupName(newGroupName);
		if (!nameValidation.isValid) {
			alert(nameValidation.error || "Invalid group name");
			return;
		}

		// Validate group description (optional)
		const descValidation = validateGroupDescription(newGroupDescription);
		if (!descValidation.isValid) {
			alert(descValidation.error || "Invalid group description");
			return;
		}

		try {
			const { error } = await supabase.from("groups").insert({
				created_by: user?.id,
				name: newGroupName.trim(),
				description: newGroupDescription.trim() || null,
			});

			if (error) {
				const errorMsg = getUserFriendlyErrorMessage(error, "creating group");
				alert(errorMsg);
			} else {
				setNewGroupName("");
				setNewGroupDescription("");
				setShowCreateDialog(false);
				fetchGroups();
			}
		} catch (error) {
			console.error("Error creating group:", error);
			const errorMsg = getUserFriendlyErrorMessage(error, "creating group");
			alert(errorMsg);
		}
	}, [newGroupName, newGroupDescription, user?.id]);

	const updateGroup = async () => {
		if (!editingGroup || !editingGroup.name.trim()) return;

		const { error } = await supabase
			.from("groups")
			.update({
				name: editingGroup.name,
				description: editingGroup.description,
			})
			.eq("id", editingGroup.id);

		if (error) {
			alert("Error updating group: " + error.message);
		} else {
			setEditingGroup(null);
			setShowEditDialog(false);
			fetchGroups();
		}
	};

	const ensureGroupOwnedByUser = async (groupId: string): Promise<boolean> => {
		if (!user?.id) return false;
		const { data: groupRow, error: groupError } = await supabase
			.from("groups")
			.select("created_by")
			.eq("id", groupId)
			.maybeSingle();

		if (groupError || !groupRow) {
			console.error("Error fetching group for ownership check:", groupError);
			return false;
		}

		return groupRow.created_by === user.id;
	};

	const deleteGroup = async () => {
		if (!deletingGroup) return;

		try {
			const owned = await ensureGroupOwnedByUser(deletingGroup.id);
			if (!owned) {
				alert("Unauthorized: you do not own this group.");
				return;
			}

			const { error } = await supabase
				.from("groups")
				.delete()
				.eq("id", deletingGroup.id)
				.eq("created_by", user?.id);

			if (error) {
				alert("Error deleting group: " + error.message);
			} else {
				setDeletingGroup(null);
				setShowDeleteDialog(false);
				fetchGroups();
			}
		} catch (error) {
			console.error("Error during group deletion:", error);
			alert("Failed to delete group. Please try again.");
		}
	};

	const addMenteeToGroup = useCallback(
		async (groupId: string, menteeId: string) => {
			if (!menteeId || !user?.id) return;

			// Verify group ownership before adding member
			const ownsGroup = await verifyGroupOwnership(user.id, groupId);
			if (!ownsGroup) {
				alert(
					"Unauthorized: You do not have permission to add members to this group.",
				);
				return;
			}

			try {
				const { error } = await supabase.from("group_memberships").insert({
					group_id: groupId,
					mentee_id: menteeId,
					added_by: user.id,
				});

				if (error) {
					const errorMsg = getUserFriendlyErrorMessage(
						error,
						"adding mentee to group",
					);
					alert(errorMsg);
				} else {
					setSelectedMenteeToAdd("");
					fetchGroups();
				}
			} catch (error) {
				console.error("Error adding mentee to group:", error);
				const errorMsg = getUserFriendlyErrorMessage(
					error,
					"adding mentee to group",
				);
				alert(errorMsg);
			}
		},
		[user?.id],
	);

	const removeMenteeFromGroup = useCallback(
		async (membershipId: string) => {
			try {
				if (!user?.id) {
					alert("Unable to remove mentee: user not authenticated.");
					return;
				}

				// Use security utility to verify permission
				const { hasPermission } = await verifyGroupMembershipPermission(
					user.id,
					membershipId,
				);
				if (!hasPermission) {
					alert(
						"Unauthorized: you do not have permission to remove this mentee.",
					);
					return;
				}

				const { error } = await supabase
					.from("group_memberships")
					.delete()
					.eq("id", membershipId);

				if (error) {
					const errorMsg = getUserFriendlyErrorMessage(
						error,
						"removing mentee from group",
					);
					alert(errorMsg);
				} else {
					fetchGroups();
				}
			} catch (error) {
				console.error("Error removing mentee from group:", error);
				const errorMsg = getUserFriendlyErrorMessage(
					error,
					"removing mentee from group",
				);
				alert(errorMsg);
			}
		},
		[user?.id],
	);

	const assignTopicToGroup = async () => {
		if (!selectedGroup) return;

		// If creating a new topic, validate and create it first
		let topicIdToAssign = selectedTopicId;

		if (isCreatingNewTopic) {
			if (!newTopicTitle.trim()) {
				alert("Please enter a topic title.");
				return;
			}

			try {
				const { data: newTopic, error: createError } = await supabase
					.from("topics")
					.insert({
						created_by: user?.id,
						name: newTopicTitle.trim(),
						description: newTopicDescription.trim() || null,
					})
					.select()
					.single();

				if (createError) {
					console.error("Error creating topic:", createError);
					alert(
						"Error creating topic: " + (createError.message || "Unknown error"),
					);
					return;
				}

				topicIdToAssign = newTopic.id;
				// Refresh topics list
				await fetchTopics();
			} catch (error: any) {
				console.error("Error creating topic:", error);
				alert(
					"Error creating topic: " +
						(error.message || "Unknown error occurred"),
				);
				return;
			}
		}

		if (!topicIdToAssign) return;

		// Get all mentees in the selected group
		const groupMentees = selectedGroup.members || [];

		try {
			// Check if a group-level assignment already exists
			const { data: existingGroupAssignment, error: groupCheckErr } =
				await supabase
					.from("topic_assignments")
					.select("id")
					.eq("topic_id", topicIdToAssign)
					.eq("group_id", selectedGroup.id)
					.maybeSingle();

			if (groupCheckErr) {
				console.error(
					"Error checking existing group assignment:",
					groupCheckErr,
				);
				throw groupCheckErr;
			}

			if (existingGroupAssignment) {
				alert("This topic is already assigned to the group.");
				setSelectedGroup(null);
				setSelectedTopicId("");
				setIsCreatingNewTopic(false);
				setNewTopicTitle("");
				setNewTopicDescription("");
				setShowAssignTopicDialog(false);
				return;
			}

			// Create a single group-level assignment row (mentee_id = null)
			const { error } = await supabase.from("topic_assignments").insert({
				topic_id: topicIdToAssign,
				mentee_id: null,
				assigned_by: user?.id,
				group_id: selectedGroup.id,
			});

			if (error) {
				console.error("Assignment error:", error);
				throw error;
			}

			alert("Assigned topic to group.");
			setSelectedGroup(null);
			setSelectedTopicId("");
			setIsCreatingNewTopic(false);
			setNewTopicTitle("");
			setNewTopicDescription("");
			setShowAssignTopicDialog(false);
			fetchAssignments();
		} catch (error: any) {
			console.error("Error assigning topic:", error);
			alert(
				"Error assigning topic: " + (error.message || "Unknown error occurred"),
			);
		}
	};

	const removeMenteeFromMentorList = async (menteeId: string) => {
		const mentee = mentees.find((m) => m.id === menteeId);
		if (!mentee) return;

		const { error } = await supabase
			.from("mentor_mentee_relationships")
			.delete()
			.eq("mentor_id", user?.id)
			.eq("mentee_id", menteeId);

		if (error) {
			alert("Error removing mentee: " + error.message);
		} else {
			alert(`${mentee.name} has been removed from your mentee list.`);
			fetchMentees();
			fetchGroups(); // Refresh groups to update member counts
		}
	};

	const getAvailableMentees = (group: Group) => {
		return mentees.filter(
			(mentee) =>
				!group.members.some((member) => member.mentee_id === mentee.id),
		);
	};

	const getGroupAssignments = (groupId: string) => {
		// Find the group to get its members
		const group = groups.find((g) => g.id === groupId);
		if (!group || !group.members) {
			return [];
		}

		// Get member IDs for this group
		const groupMenteeIds = new Set(
			group.members.map((member) => member.mentee_id),
		);

		// Get assignments that were created specifically for this group
		const groupAssignments = assignments.filter(
			(assignment) => assignment.group_id === groupId,
		);

		// Group assignments by topic to avoid duplicates and get unique topics assigned to this group
		const uniqueTopics = new Map();
		groupAssignments.forEach((assignment) => {
			if (!uniqueTopics.has(assignment.topic_id)) {
				// For the displayed assignment, calculate completion stats for this group
				const topicAssignments = groupAssignments.filter(
					(a) => a.topic_id === assignment.topic_id,
				);
				const totalMentees = group.members.length;
				const assignedMentees = topicAssignments.filter((a) =>
					groupMenteeIds.has(a.mentee_id),
				).length;

				uniqueTopics.set(assignment.topic_id, {
					...assignment,
					completion_stats: {
						total_mentees: totalMentees,
						completed_count: assignedMentees,
						completion_percentage:
							totalMentees > 0
								? Math.round((assignedMentees / totalMentees) * 100)
								: 0,
					},
				});
			}
		});

		return Array.from(uniqueTopics.values());
	};

	const getGroupAssignedTopicDetails = (group: Group) => {
		const members = group.members || [];
		const totalMentees = members.length;
		const menteeIds = new Set(members.map((m) => m.mentee_id));

		// Find unique topic ids relevant to this group (direct group assignments only)
		const topicIds = new Set<string>();
		assignments.forEach((a) => {
			if (a.group_id === group.id) topicIds.add(a.topic_id);
		});

		const details = Array.from(topicIds).map((tid) => {
			const topic = topics.find((t) => t.id === tid);
			const assignedCount = assignments.filter(
				(a) => a.topic_id === tid && a.group_id === group.id,
			).length;
			const completionPercentage =
				totalMentees > 0 ? Math.round((assignedCount / totalMentees) * 100) : 0;
			return {
				topic_id: tid,
				title: (topic as any)?.title || (topic as any)?.name || "Unknown Topic",
				description: (topic as any)?.description || "",
				status: assignedCount > 0 ? "Active" : "Inactive",
				totalMentees,
				assignedCount,
				completionPercentage,
			};
		});

		return details;
	};

	const getStatusIcon = (completionPercentage: number) => {
		if (completionPercentage === 100) {
			return <CheckCircle className="w-4 h-4 text-green-600" />;
		} else if (completionPercentage > 50) {
			return <Clock className="w-4 h-4 text-yellow-600" />;
		} else {
			return <AlertTriangle className="w-4 h-4 text-red-600" />;
		}
	};

	if (loading) {
		return (
			<div className="text-center py-8">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-muted-foreground">Loading group management...</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex justify-between items-start">
				<div>
					<h2 className="text-2xl font-bold">Group Management</h2>
					<p className="text-muted-foreground">
						Create and manage your mentee groups
					</p>
				</div>
				<Button onClick={() => setShowCreateDialog(true)}>
					<Plus className="w-4 h-4 mr-2" />
					Create New Group
				</Button>
			</div>

			{/* Groups Summary Table */}
			<Card>
				<CardHeader>
					<CardTitle>All Groups</CardTitle>
					<CardDescription>Overview of all groups you manage</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="rounded-md border overflow-auto">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Date Created</TableHead>
									<TableHead>Total Members</TableHead>
									<TableHead>Progress</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{groups.map((g) => {
									const details = getGroupAssignedTopicDetails(g);
									const progress =
										details.length > 0
											? Math.round(
													details.reduce(
														(acc, d) => acc + d.completionPercentage,
														0,
													) / details.length,
												)
											: 0;

									return (
										<TableRow key={g.id} className="hover:bg-muted/50">
											<TableCell className="font-medium">{g.name}</TableCell>
											<TableCell>
												{new Date(g.created_at).toLocaleDateString()}
											</TableCell>
											<TableCell>{g.member_count}</TableCell>
											<TableCell className="w-48">
												<div className="flex items-center space-x-2">
													<div className="flex-1">
														<Progress value={progress} />
													</div>
													<div className="text-xs text-muted-foreground w-12 text-right">
														{progress}%
													</div>
												</div>
											</TableCell>
											<TableCell>
												<Button
													size="sm"
													variant="outline"
													onClick={() => navigate(`/mentor/group/${g.id}`)}
												>
													View
												</Button>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			{/* Create Group Dialog */}
			<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Create New Group</DialogTitle>
						<DialogDescription>
							Create a group to organize your mentees and assign topics
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label htmlFor="groupName">Group Name</Label>
							<Input
								id="groupName"
								value={newGroupName}
								onChange={(e) => setNewGroupName(e.target.value)}
								placeholder="e.g. Future of Ex Comm Group"
							/>
						</div>
						<div>
							<Label htmlFor="groupDescription">Description (Optional)</Label>
							<Input
								id="groupDescription"
								value={newGroupDescription}
								onChange={(e) => setNewGroupDescription(e.target.value)}
								placeholder="Brief description of the group"
							/>
						</div>
						<div className="flex justify-end space-x-2">
							<Button
								variant="outline"
								onClick={() => setShowCreateDialog(false)}
							>
								Cancel
							</Button>
							<Button onClick={createGroup} disabled={!newGroupName.trim()}>
								Create New Group
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Edit Group Dialog */}
			<Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Edit Group</DialogTitle>
						<DialogDescription>
							Update group name and description
						</DialogDescription>
					</DialogHeader>
					{editingGroup && (
						<div className="space-y-4">
							<div>
								<Label htmlFor="editGroupName">Group Name</Label>
								<Input
									id="editGroupName"
									value={editingGroup.name}
									onChange={(e) =>
										setEditingGroup({
											...editingGroup,
											name: e.target.value,
										})
									}
								/>
							</div>
							<div>
								<Label htmlFor="editGroupDescription">Description</Label>
								<Input
									id="editGroupDescription"
									value={editingGroup.description}
									onChange={(e) =>
										setEditingGroup({
											...editingGroup,
											description: e.target.value,
										})
									}
								/>
							</div>
							<div className="flex justify-end space-x-2">
								<Button
									variant="outline"
									onClick={() => setShowEditDialog(false)}
								>
									Cancel
								</Button>
								<Button
									onClick={updateGroup}
									disabled={!editingGroup.name.trim()}
								>
									Save Changes
								</Button>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Delete Group Dialog */}
			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Group</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{deletingGroup?.name}"? This will
							remove all members and topic assignments. This action cannot be
							undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setDeletingGroup(null)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={deleteGroup}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Delete Group
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Assign Topic Dialog */}
			<Dialog
				open={showAssignTopicDialog}
				onOpenChange={(open) => {
					setShowAssignTopicDialog(open);
					if (!open) {
						// Reset form when dialog closes
						setIsCreatingNewTopic(false);
						setSelectedTopicId("");
						setNewTopicTitle("");
						setNewTopicDescription("");
					}
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Assign Topic to {selectedGroup?.name}</DialogTitle>
						<DialogDescription>
							Topics guide mentees through structured reflection exercises.
							Select an existing topic or create a new one to assign to this
							group.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label>Select Topic</Label>
							<Select
								value={isCreatingNewTopic ? "new_topic" : selectedTopicId}
								onValueChange={(value) => {
									if (value === "new_topic") {
										setIsCreatingNewTopic(true);
										setSelectedTopicId("");
									} else {
										setIsCreatingNewTopic(false);
										setSelectedTopicId(value);
									}
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="Choose a topic" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="new_topic">New Topic</SelectItem>
									{topics.map((topic) => {
										const isAssigned = selectedGroup
											? getGroupAssignments(selectedGroup.id).some(
													(a) => a.topic_id === topic.id,
												)
											: false;

										return (
											<SelectItem
												key={topic.id}
												value={topic.id}
												disabled={isAssigned}
											>
												{topic.title}
												{isAssigned ? " (assigned)" : ""}
											</SelectItem>
										);
									})}
								</SelectContent>
							</Select>
						</div>

						{isCreatingNewTopic && (
							<div className="space-y-4 pt-2 border-t">
								<div>
									<Label htmlFor="newTopicTitle">Topic Title *</Label>
									<Input
										id="newTopicTitle"
										value={newTopicTitle}
										onChange={(e) => setNewTopicTitle(e.target.value)}
										placeholder="Enter topic title"
									/>
								</div>
								<div>
									<Label htmlFor="newTopicDescription">
										Description (Optional)
									</Label>
									<Textarea
										id="newTopicDescription"
										value={newTopicDescription}
										onChange={(e) => setNewTopicDescription(e.target.value)}
										placeholder="Enter topic description"
										className="min-h-[100px]"
									/>
								</div>
							</div>
						)}

						<div className="flex justify-end space-x-2">
							<Button
								variant="outline"
								onClick={() => {
									setShowAssignTopicDialog(false);
									setIsCreatingNewTopic(false);
									setSelectedTopicId("");
									setNewTopicTitle("");
									setNewTopicDescription("");
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={assignTopicToGroup}
								disabled={
									isCreatingNewTopic ? !newTopicTitle.trim() : !selectedTopicId
								}
							>
								{isCreatingNewTopic ? "Create & Assign Topic" : "Assign Topic"}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Confirm Remove Mentee Dialog */}
			<AlertDialog
				open={showConfirmRemoveMenteeDialog}
				onOpenChange={setShowConfirmRemoveMenteeDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove Mentee</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to remove "{menteeToRemove?.name}" from your
							mentee list? This will remove them from all groups and delete the
							mentor-mentee relationship.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							onClick={() => {
								setMenteeToRemove(null);
								setShowConfirmRemoveMenteeDialog(false);
							}}
						>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={async () => {
								if (menteeToRemove) {
									await removeMenteeFromMentorList(menteeToRemove.id);
									setMenteeToRemove(null);
									setShowConfirmRemoveMenteeDialog(false);
								}
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							Remove Mentee
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
